/**
 * Thread pane (3rd column). Ported from the storybook `final/_helpdesk.tsx`
 * `Thread` / `ThreadHeader` / `Bubble` / `DecisionBar` — the header, the
 * bubble styles, the scroll container and the footer swap (Composer vs
 * DecisionBar) are kept verbatim; the data is the real session transcript
 * (`useSessionMessages`) mapped onto the storybook bubble roles.
 *
 * Message → bubble: role `user` → customer (left, muted); role `assistant` →
 * AI (right, info-tint, MagicStar); role `human_agent` → human (right, sender
 * avatar); any `subtype` (handoff_join / handoff_handback / …) → a centered
 * event pill. Rich `content` cards render as their text (never crash).
 */
import * as React from "react";
import { Avatar, AvatarFallback, Button, Input, Skeleton, cn } from "@cometchat-ai/ui-dashboard";
import { useTranslation } from "react-i18next";

import { GitBranch, Note, Scale, Smile, Sparkles, X } from "../components/icons";
import { Check } from "../components/icons";
import type { SessionMessage } from "../agents/api/types";
import type { InboxDecision, SessionStatus } from "../handoff/api";
import { Composer } from "./Composer";
import { ChannelPill, HdAvatar, StatusPill, fmtClock } from "./helpers";
import type { ViewId } from "./InboxViewsRail";

/* ------------------------------------------------------------------ header --- */

function ThreadHeader({
  name,
  subject,
  channel,
  status,
}: {
  name: string;
  subject: string | null;
  channel: string;
  status: SessionStatus;
}) {
  return (
    <>
      <div className="flex h-12 shrink-0 items-center gap-2.5 border-b border-border px-4.5">
        <HdAvatar name={name} size={28} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold tracking-[0.01em] text-foreground">
            {name}
          </span>
          {subject ? (
            <span className="truncate text-xs text-muted-foreground">{subject}</span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-border px-4.5 py-2">
        <StatusPill status={status} />
        <ChannelPill channel={channel} />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ bubble --- */

function humanizeSubtype(subtype: string): string {
  return subtype.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function messageText(msg: SessionMessage): string {
  if (msg.text && msg.text.trim()) return msg.text;
  // Rich content cards carry no plain text — show a neutral placeholder.
  if (msg.content != null) return "…";
  return "";
}

function Bubble({ msg, custName }: { msg: SessionMessage; custName: string }) {
  const { t } = useTranslation("workspace");

  // Untagged `system` rows are AI scaffolding — the proactive-resume trigger
  // ("A team decision was just resolved…"), steer re-run prompts, etc. They
  // drive the model, not the operator; tagged markers (subtype set) still show
  // as event pills below. Never render a raw system prompt in the transcript.
  if (msg.role === "system" && !msg.subtype) return null;

  if (msg.role === "internal_note") {
    // A ruling recorded by the Decisions inbox carries `subtype: "decision"` —
    // label it "Decision · <agent>" with a scale glyph instead of "Internal note".
    // CSAT feedback (`subtype: "csat_response"`) is authored by the CUSTOMER —
    // attribute it to them, not to an operator.
    const isDecision = msg.subtype === "decision";
    const isFeedback = msg.subtype === "csat_response";
    const author = isFeedback ? custName : (msg.sender?.display_name ?? "You");
    const noteTime = fmtClock(msg.created_at);
    const HeaderIcon = isDecision ? Scale : isFeedback ? Smile : Note;
    return (
      // Notes/decisions are operator-authored → right side like agent replies;
      // customer feedback is end-user-authored → left side like their messages.
      <div
        className={cn("flex items-start gap-2.5", !isFeedback && "flex-row-reverse")}
        data-testid="conv-note-bubble"
      >
        <HdAvatar name={author} src={isFeedback ? undefined : msg.sender?.avatar_url} size={28} />
        <div className="max-w-[80%]">
          <div className="flex items-center gap-1.5 rounded-t-lg bg-[color:color-mix(in_srgb,var(--color-warning)_12%,var(--card))] px-3 pb-4 pt-1.5 text-xs font-medium text-[color:var(--color-warning)]">
            <HeaderIcon className="h-3 w-3" aria-hidden="true" />{" "}
            {isDecision
              ? t("helpdesk.composer.decisionHeader", { name: author })
              : isFeedback
                ? t("helpdesk.composer.feedbackHeader", { name: author })
                : t("helpdesk.composer.noteHeader", { name: author })}
          </div>
          <div className="relative -mt-2.5 rounded-lg border border-[color:color-mix(in_oklch,var(--color-warning)_18%,transparent)] bg-[color:color-mix(in_srgb,var(--color-warning)_5%,var(--card))] px-3 py-2">
            <p className="whitespace-pre-wrap break-words text-2sm text-foreground">
              {messageText(msg)}
            </p>
            {noteTime ? (
              <div className="mt-0.5 text-right text-2xs text-[color:var(--color-fg-subtle)]">
                {noteTime}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (msg.subtype) {
    return (
      <div className="my-5 flex justify-center">
        <span className="max-w-full rounded-full bg-muted px-2.5 py-1 text-center text-xs leading-relaxed text-muted-foreground">
          <GitBranch className="mr-1 inline-block h-3 w-3 -translate-y-px" aria-hidden="true" />
          {msg.text?.trim() ? msg.text : humanizeSubtype(msg.subtype)} ·{" "}
          <b>{fmtClock(msg.created_at)}</b>
        </span>
      </div>
    );
  }

  const isCustomer = msg.role === "user";
  const isAi = msg.role === "assistant";
  const text = messageText(msg);
  const time = fmtClock(msg.created_at);

  return (
    <div className={cn("flex items-start gap-2.5", isCustomer ? "flex-row" : "flex-row-reverse")}>
      {isCustomer ? (
        <HdAvatar name={custName} size={28} />
      ) : isAi ? (
        <Avatar className="h-7 w-7 shrink-0 rounded-lg">
          <AvatarFallback className="rounded-lg bg-muted text-foreground">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </AvatarFallback>
        </Avatar>
      ) : (
        <HdAvatar
          name={msg.sender?.display_name ?? "Agent"}
          src={msg.sender?.avatar_url}
          size={28}
        />
      )}
      <div
        className={cn("flex max-w-[78%] flex-col gap-1", isCustomer ? "items-start" : "items-end")}
      >
        {!isCustomer && !isAi && msg.sender?.display_name ? (
          <span className="px-1 text-xxs font-medium text-muted-foreground">
            {msg.sender.display_name}
          </span>
        ) : null}
        <div
          className={cn(
            "rounded-2xl px-3.5 py-2 text-2sm leading-relaxed",
            isCustomer
              ? "bg-muted text-foreground"
              : "bg-[color:color-mix(in_oklch,var(--color-info)_12%,transparent)] text-foreground",
          )}
        >
          {text ? <p className="whitespace-pre-wrap break-words">{text}</p> : null}
          {time ? (
            <span className="mt-1 block text-right text-xs text-[color:var(--color-fg-subtle)]">
              {time}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- decision bar --- */

function DecisionBar({
  decision,
  deciding,
  onApprove,
  onDeny,
}: {
  decision: InboxDecision;
  deciding: boolean;
  onApprove: (note?: string) => void;
  onDeny: (note?: string) => void;
}) {
  const { t } = useTranslation("workspace");
  const [note, setNote] = React.useState("");
  React.useEffect(() => setNote(""), [decision.id]);
  const decide = (fn: (note?: string) => void) => fn(note.trim() || undefined);

  return (
    <div className="shrink-0 px-4.5 py-3">
      <div className="w-full rounded-lg border border-border bg-muted px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Scale className="h-3.5 w-3.5" />
          {t("decisions.needed")}
        </div>
        <p className="mt-0.5 text-2sm text-foreground">{decision.questionText}</p>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          spellCheck={false}
          placeholder={t("decisions.notePlaceholder")}
          data-testid="helpdesk-decision-note"
          className="mt-2.5 h-9 w-full bg-background text-2sm placeholder:text-2sm placeholder:text-[color:var(--color-fg-subtle)]"
        />
        <div className="mt-2.5 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={deciding}
            onClick={() => decide(onDeny)}
            data-testid="helpdesk-decision-deny"
            className="h-9 w-28 shrink-0 gap-1.5 border-[color:color-mix(in_oklch,var(--color-danger)_35%,transparent)] text-[color:var(--color-danger)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--color-danger)_8%,transparent)] hover:text-[color:var(--color-danger)] active:scale-100"
          >
            <X className="h-4 w-4" /> {t("decisions.deny")}
          </Button>
          <Button
            type="button"
            disabled={deciding}
            onClick={() => decide(onApprove)}
            data-testid="helpdesk-decision-approve"
            className="h-9 w-28 shrink-0 gap-1.5 bg-foreground text-background transition-opacity hover:bg-foreground hover:opacity-90 active:scale-100"
          >
            <Check className="h-4 w-4" /> {t("decisions.approve")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ thread --- */

export function Thread({
  name,
  subject,
  channel,
  status,
  view,
  messages,
  isLoading,
  isError,
  onRetry,
  sending,
  sendingNote,
  onSend,
  onSendNote,
  onTakeover,
  onHandback,
  onResolve,
  onClaim,
  canTakeover,
  canHandback,
  canResolve,
  canClaim,
  canNote,
  isOwnedByMe,
  canReplyAll,
  ownerName,
  decision,
  deciding,
  onApprove,
  onDeny,
  canRule,
}: {
  name: string;
  subject: string | null;
  channel: string;
  status: SessionStatus;
  view: ViewId;
  messages: SessionMessage[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  sending: boolean;
  sendingNote: boolean;
  onSend: (text: string) => void;
  onSendNote: (text: string) => void;
  onTakeover: () => void;
  onHandback: () => void;
  onResolve: () => void;
  onClaim: () => void;
  canTakeover: boolean;
  canHandback: boolean;
  canResolve: boolean;
  canClaim: boolean;
  canNote: boolean;
  isOwnedByMe: boolean;
  canReplyAll: boolean;
  ownerName: string | null;
  decision?: InboxDecision;
  deciding: boolean;
  onApprove: (note?: string) => void;
  onDeny: (note?: string) => void;
  canRule: boolean;
}) {
  const { t } = useTranslation("workspace");
  const endRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  return (
    <div className="flex min-w-72 flex-[2] flex-col">
      <ThreadHeader name={name} subject={subject} channel={channel} status={status} />

      <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4.5 py-4">
        {isLoading ? (
          <div className="space-y-4 py-2">
            <Skeleton className="h-16 w-2/3" />
            <Skeleton className="ml-auto h-20 w-3/4" />
            <Skeleton className="h-14 w-1/2" />
          </div>
        ) : isError ? (
          <div className="flex h-full items-center justify-center">
            <button
              type="button"
              onClick={onRetry}
              className="text-2sm text-[color:var(--color-danger)] underline-offset-4 hover:underline"
            >
              {t("helpdesk.loadError")}
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-2sm text-muted-foreground">
            {t("helpdesk.empty")}
          </div>
        ) : (
          messages.map((m) => <Bubble key={m.id} msg={m} custName={name} />)
        )}
        <div ref={endRef} />
      </div>

      {view === "decisions" && decision ? (
        canRule ? (
          <DecisionBar
            decision={decision}
            deciding={deciding}
            onApprove={onApprove}
            onDeny={onDeny}
          />
        ) : null
      ) : (
        <Composer
          status={status}
          custName={name}
          sending={sending}
          sendingNote={sendingNote}
          onSend={onSend}
          onSendNote={onSendNote}
          onTakeover={onTakeover}
          onHandback={onHandback}
          onResolve={onResolve}
          onClaim={onClaim}
          canTakeover={canTakeover}
          canHandback={canHandback}
          canResolve={canResolve}
          canClaim={canClaim}
          canNote={canNote}
          isOwnedByMe={isOwnedByMe}
          canReplyAll={canReplyAll}
          ownerName={ownerName}
        />
      )}
    </div>
  );
}
